/**
 * BigSplice Chat Page
 * Main chat interface component with navigation sidebar, session panel, and message area
 */

import React, { useRef, useEffect, useState } from 'react';
import { useChatState } from '../../hooks/useChatState';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { DeleteConfirmation } from './DeleteConfirmation';
import { UploadHelper } from './UploadHelper';
import { Button } from '../ui/button';
import { TopNav } from '../ui/top-nav';
import {
  MessageSquarePlus,
  MessageSquare,
  Trash2,
  FileText,
  X,
  Loader2,
  Sparkles,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const ChatPage: React.FC = () => {
  const {
    sessions,
    currentSession,
    currentSessionId,
    isLoading,
    error,
    selectedFile,
    selectedFileKey,
    deleteConfirmation,
    uploadHelper,
    createNewSession,
    switchSession,
    deleteSession,
    sendMessage,
    selectFile,
    confirmDeleteStep1,
    confirmDeleteStep2,
    cancelDelete,
    updateDeleteTypedText,
    openUploadHelper,
    closeUploadHelper,
    setUploadMethod,
    updateDownloadProgress,
  } = useChatState();

  const [sessionsCollapsed, setSessionsCollapsed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentSession?.messages]);

  const handleSendMessage = async (content: string) => {
    await sendMessage(content);
  };

  const formatSessionDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      <TopNav />

      <div className="flex" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Chat Sessions Panel */}
        <div className={cn(
          'border-r border-border bg-muted/30 flex flex-col transition-all duration-200 flex-shrink-0',
          sessionsCollapsed ? 'w-0 overflow-hidden' : 'w-56'
        )}>
          {/* New Chat Button */}
          <div className="p-3 border-b border-border">
            <Button
              onClick={() => createNewSession()}
              className="w-full justify-start gap-2"
              variant="outline"
              size="sm"
            >
              <MessageSquarePlus className="h-4 w-4" />
              New Chat
            </Button>
          </div>

          {/* Session List */}
          <div className="flex-1 overflow-y-auto p-2">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={cn(
                  'group flex items-center gap-2 p-2 rounded cursor-pointer mb-1',
                  'hover:bg-muted transition-colors',
                  session.id === currentSessionId && 'bg-muted border border-border'
                )}
                onClick={() => switchSession(session.id)}
              >
                <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{session.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatSessionDate(session.updatedAt)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSession(session.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>

          {/* Selected File Indicator */}
          {selectedFile && (
            <div className="p-3 border-t border-border bg-primary/5">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="flex-1 truncate font-medium text-xs">{selectedFile}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 flex-shrink-0"
                  onClick={() => selectFile(null, null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Sub-header with collapse toggle */}
          <div className="h-12 border-b border-border flex items-center justify-between px-4 bg-muted/20">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSessionsCollapsed(!sessionsCollapsed)}
              >
                {sessionsCollapsed ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </Button>
              <span className="text-sm font-medium text-muted-foreground">
                {currentSession?.title || 'New Chat'}
              </span>
            </div>
            {selectedFile && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                <FileText className="h-3 w-3" />
                <span className="truncate max-w-[200px]">{selectedFile}</span>
              </div>
            )}
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto">
            {currentSession?.messages.length === 0 ? (
              <WelcomeScreen onSendMessage={handleSendMessage} />
            ) : (
              <div className="max-w-3xl mx-auto py-6 px-4">
                {currentSession?.messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    deleteConfirmation={deleteConfirmation}
                    onConfirmDeleteStep1={confirmDeleteStep1}
                    onConfirmDeleteStep2={confirmDeleteStep2}
                    onCancelDelete={cancelDelete}
                    onUpdateDeleteTypedText={updateDeleteTypedText}
                    onOpenUploadHelper={openUploadHelper}
                    onSelectFile={selectFile}
                    onSendMessage={handleSendMessage}
                  />
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex items-center gap-2 py-4 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Thinking...</span>
                  </div>
                )}

                {/* Error display */}
                {error && (
                  <div className="py-3 px-4 bg-destructive/10 text-destructive rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <ChatInput
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            selectedFile={selectedFile}
            selectedFileKey={selectedFileKey}
            onSelectFile={selectFile}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.targetFile && (
        <DeleteConfirmation
          fileName={deleteConfirmation.targetFile}
          fileType={deleteConfirmation.targetType}
          step={deleteConfirmation.step}
          typedText={deleteConfirmation.typedText}
          onConfirmStep1={confirmDeleteStep1}
          onConfirmStep2={confirmDeleteStep2}
          onCancel={cancelDelete}
          onUpdateTypedText={updateDeleteTypedText}
        />
      )}

      {/* Upload Helper Modal */}
      {uploadHelper.isOpen && (
        <UploadHelper
          isOpen={uploadHelper.isOpen}
          method={uploadHelper.method}
          downloadProgress={uploadHelper.downloadProgress}
          onClose={closeUploadHelper}
          onSelectMethod={setUploadMethod}
          onUpdateProgress={updateDownloadProgress}
        />
      )}
    </div>
  );
};

// Welcome screen component
const WelcomeScreen: React.FC<{ onSendMessage: (msg: string) => void }> = ({
  onSendMessage,
}) => {
  const quickActions = [
    { label: 'List my files', command: 'list files' },
    { label: 'Show upload options', command: 'upload help' },
    { label: 'View usage stats', command: 'my usage' },
    { label: 'Get help', command: 'help' },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-4">
      <div className="max-w-2xl text-center">
        <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Welcome to BigSplice Chat</h2>
        <p className="text-muted-foreground mb-8">
          Your conversational interface to all BigSplice features. Ask questions
          about your data, run analyses, create visualizations, and more.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {quickActions.map((action) => (
            <Button
              key={action.command}
              variant="outline"
              className="h-auto py-3 px-4 justify-start"
              onClick={() => onSendMessage(action.command)}
            >
              <span className="text-left">{action.label}</span>
            </Button>
          ))}
        </div>

        <p className="text-sm text-muted-foreground">
          Try typing commands like{' '}
          <code className="bg-muted px-1 py-0.5 rounded">list files</code>,{' '}
          <code className="bg-muted px-1 py-0.5 rounded">head 10</code>, or ask
          natural language questions about your data.
        </p>
      </div>
    </div>
  );
};

export default ChatPage;
